import { bindable, CustomElement, IEventAggregator, IObserverLocator, inject } from 'aurelia';
import { getLogger } from '../../common/logging';
import { FormFieldChanged } from '../../messages/form-field-changed';
import { FieldInvalid } from '../../messages/field-invalid';
import { registerField, unregisterField } from '../../common/element-field';
import { elementId } from '../../common/element-id';

let log = getLogger('form-field');

@inject(Element, IObserverLocator, IEventAggregator)
export class FormFieldCustomAttribute {
  @bindable model;
  @bindable field;

  constructor(element, observerLocator, ea) {
    this.revision = 0;
    this.element = element;
    this.observerLocator = observerLocator;
    this.ea = ea;
    this.timeout = new Timeout();
  }

  created() {
    if (['text', 'email'].indexOf(this.element.type) > -1) {
      if (!this.element.attributes.maxlength) {
        this.element.setAttribute('maxlength', 100);
      }
      // Ett fält som anger vad det frågar efter, t.ex. autocomplete="email", får fyllas i av webbläsaren (WCAG 1.3.5).
      if (!this.element.attributes.autocomplete) {
        this.element.setAttribute('autocomplete', 'off');
      }
    }
    this.saveAsBatch = this.element.attributes['form-field-batching'] !== undefined;

  }

  binding() {
    this.fieldName = this.field;
    this.baseModel = this.model;
    registerField(this.element, this.baseModel, this.fieldName);

    if (this.baseModel) {
      if(this.baseModel.groupName){
        this.groupName = this.baseModel.groupName;
        this.groupIndex = this.baseModel.groupIndex;
      }
    }

    this.observer = this.observerLocator.getObserver(this.baseModel, this.fieldName);
    this.observer.subscribe(this);

    this.fieldInvalidSubscription = this.ea.subscribe(FieldInvalid, m => this.onFieldInvalid(m));
  }

  unbinding() {
    unregisterField(this.element);
    this.fieldInvalidSubscription.dispose();
    // An input that leaves the page when another field changes is still there, under Aurelia 1, while the
    // handlers of that change run. One that clears the field then is saved, so this waits for them. The
    // browser runs microtasks between the listeners of an event, so only a task outlasts all of them.
    setTimeout(() => this.observer.unsubscribe(this));
  }

  handleChange(newValue, oldValue) {
    this.onValueChanged(newValue, oldValue);
  }

  onValueChanged(newValue, oldValue) {
    if (newValue === oldValue) return;

    if (!this.saveAsBatch) {
      this.timeout.clear();
      this.timeout.set(() => this.publishFieldChange(newValue, oldValue, this.saveAsBatch), 300);
    } else {
      this.publishFieldChange(newValue, oldValue, this.saveAsBatch);
    }

    if (this.element.tagName !== 'INPUT' && this.element.tagName !== 'SELECT' && this.element.tagName !== 'TEXTAREA') {
      this.element.tagName === 'INPUT-FIELD' || this.element.tagName === 'INPUT-TEXTAREA'
        ? CustomElement.for(this.element).viewModel.removeErrorClass()
        : this.element.children[0]?.classList.remove('has-error');
      return;
    }
    this.element.classList.remove('has-error');
  }

  onFieldInvalid(m) {
    if (m.fieldName !== this.fieldName || m.groupName !== this.groupName || m.groupIndex !== this.groupIndex) return;

    if (this.element.tagName !== 'INPUT' && this.element.tagName !== 'SELECT' && this.element.tagName !== 'TEXTAREA') {
      this.element.tagName === 'INPUT-FIELD' || this.element.tagName === 'INPUT-TEXTAREA'
        ? CustomElement.for(this.element).viewModel.addErrorClass()
        : this.element.children[0].classList.add('has-error');

      return;
    }

    this.element.classList.add('has-error');
  }

  publishFieldChange(newValue, oldValue, saveAsBatch) {
    let message = new FormFieldChanged(this.fieldName, newValue, oldValue, this.groupName, this.groupIndex, elementId(this.element), saveAsBatch);
    this.ea.publish(message);
  }
}

class Timeout {
  contstructor() {
    this.current = 0;
  }

  set(callback, timeout) {
    this.current = setTimeout(callback, timeout);
  }

  clear() {
    clearTimeout(this.current);
    this.current = 0;
  }
}
